import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from 'emailjs-com';

// Styled Components
const PricingSection = styled.section`
  padding: 2rem 10%;
  background-color: #f4f7fb;
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled.h2`
  font-size: 36px;
  margin-bottom: 2.5rem;
  color: #333;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  justify-items: center;
`;

const PricingCard = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  text-align: center;
  width: 100%;
  max-width: 300px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 8px 16px rgb(88, 199, 192);
  }
`;

const PlanName = styled.h3`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
`;

const Price = styled.p`
  font-size: 28px;
  color: #007bff;
  margin-bottom: 1rem;
`;

const FeaturesList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 1.5rem;

  li {
    margin: 10px 0;
    color: #333;
    font-weight: 500;
  }
`;

const GetStartedButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: rgb(88, 199, 192);
  color: white;
  font-weight: bold;
  border: none;
  border-radius: 30px;
  cursor: pointer;

  &:hover {
    background-color: rgb(21, 21, 22);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
`;

const Input = styled.input`
  width: 90%;
  padding: 0.9rem;
  margin: 0.5rem 0;
  border: 1px solid #ccc;
  border-radius: 7px;
`;

const SubmitButton = styled.button`
  padding: 0.8rem 1.5rem;
  background-color: rgb(88, 199, 192);
  color: white;
  font-weight: bold;
  border: none;
  border-radius: 30px;
  cursor: pointer;

  &:hover {
    background-color: rgb(21, 21, 22);
  }
`;

const Pricing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailPattern.test(email);
  };

  const validatePhone = (phone) => {
    const phonePattern = /^[0-9]{10}$/;
    return phonePattern.test(phone);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let valid = true;
    let newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
      valid = false;
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number.';
      valid = false;
    }

    setErrors(newErrors);

    if (valid) {
      emailjs
        .send(
          'service_ol8d0xl',
          'template_j33zuts',
          formData,
          'KzLOHoDyH8Fdt2le6'
        )
        .then(
          (result) => {
            alert('Details submitted successfully!');
            setIsModalOpen(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
            }); // Reset form data
          },
          (error) => {
            alert('Failed to send details. Please try again.');
            console.error(error);
          }
        );
    }
  };

  const handleOverlayClick = (e) => {
    // Close modal if overlay is clicked
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <PricingSection id="Pricing">
      <Title>Plans & Pricing</Title>
      <PricingGrid>
        <PricingCard>
          <PlanName>-------</PlanName>
          <Price></Price>
          <FeaturesList>
            <li>Hyperlocal Inventory Placement</li>
            <li>AI-powered forecasting</li>
            <li>Analytics</li>
          </FeaturesList>
          <GetStartedButton onClick={() => setIsModalOpen(true)}>
            Contact Us
          </GetStartedButton>
        </PricingCard>
      </PricingGrid>

      {isModalOpen && (
        <ModalOverlay onClick={handleOverlayClick}>
          <ModalContent>
            <h2>Contact Us</h2>
            <form onSubmit={handleSubmit}>
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              {errors.email && <p style={{ color: 'red' }}>{errors.email}</p>}

              <Input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
              {errors.phone && <p style={{ color: 'red' }}>{errors.phone}</p>}

              <SubmitButton type="submit">Submit</SubmitButton>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </PricingSection>
  );
};

export default Pricing;
